import "swiper/css";
import ServiceCard from '@/Components/Homepage/ServiceCard';
import { SERVICE_ITEMS } from '@/Lib/siteData';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useEffect, useRef, useState } from "react";
import FillDots from "@/Components/Svg/FillDots";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, type IconDefinition } from "@fortawesome/free-solid-svg-icons";
import type { Swiper as SwiperType } from "swiper/types";
import { useAppContext } from "@/Components/Providers/AppProvider";

type Props = {

}

export default function Services({}: Props) {
    const { homepageAnchors: { services: sectionRef }} = useAppContext();
    const [isSwiping, setIsSwiping] = useState(false);
    const [dotsWidth, setDotsWidth] = useState(0);
    const [dotsHeight, setDotsHeight] = useState(0);
    const [swiper, setSwiper] = useState<SwiperType | null>(null);
    const [currentService, setCurrentService] = useState('');
    const [currentServiceIcon, setCurrentServiceIcon] = useState<IconDefinition | null>(null);

    useEffect(() => {
        if (sectionRef.current) {
            setDotsWidth(sectionRef.current.getBoundingClientRect().width);
            setDotsHeight(sectionRef.current.getBoundingClientRect().height);
        }
    }, [dotsWidth, dotsHeight, sectionRef]);

    return <section id="services" ref={sectionRef} className="relative min-w-full min-h-[calc(100dvh-100px-4*var(--spacing))] mask-(--mask-grid-edges-vertical) mb-16 px-2 md:px-4">
        <FillDots width={dotsWidth} height={dotsHeight} density={dotsHeight / 16} size={2} color="#F57C00" className="absolute top-0 left-0 z-5" />
        <div className="relative w-full h-full px-4 md:px-8 z-10 mask-(--mask-grid-edges-horizontal-tight)">
            <div className="relative h-[80px] md:h-[200px] px-4 md:px-8 bg-linear-to-t from-lighter to-transparent">
                <div className="absolute top-0 -left-4 md:-left-8 w-8 md:w-16 h-full border-b-2 border-brand box-content bg-lighter"></div>
                <div className="border-x-2 border-brand w-full h-full grid grid-cols-1 md:grid-cols-3 grid-rows-1 bg-">
                    <div className="hidden md:block border-r-2"></div>
                    <div className="hidden md:block"></div>
                    <div className="hidden md:block border-l-2"></div>
                </div>
                <div className="absolute top-0 -right-4 md:-right-8 w-8 md:w-16 h-full border-b-2 border-brand box-content bg-lighter"></div>
            </div>

            <div className="px-4 md:px-8 border-t-2">
                <div className="relative p-4 md:p-8 font-title text-light uppercase tracking-wider text-2xl md:text-5xl font-medium border-x-2 border-b-2 border-brand bg-lighter">
                    <div className="absolute top-0 -left-8 md:-left-16 w-8 md:w-16 h-full border-b-2 border-brand bg-lighter -ml-[2px] box-content"></div>
                    <div className="flex flex-row justify-between items-center gap-2 md:gap-8">
                        <div className="h-full text-brand content-end flex flex-row justify-start items-center gap-2 md:gap-8">
                            <h1>Our Services</h1>
                            <h2 className="hidden md:inline text-lg md:text-2xl">{currentService}</h2>
                        </div>
                        {currentServiceIcon && <FontAwesomeIcon icon={currentServiceIcon} size="xs" className="hidden md:inline text-brand" />}
                    </div>
                    <div className="absolute top-0 -right-8 md:-right-16 w-8 md:w-16 h-full border-b-2 border-brand bg-lighter -mr-[2px] box-content"></div>
                </div>
                <div className="relative">
                    <div className="absolute top-0 -left-8 md:-left-16 w-8 md:w-16 h-full border-b-2 border-brand bg-lighter -ml-[2px] box-content"></div>
                    <Swiper
                        onSwiper={(swiper) => setSwiper(swiper)}
                        slidesPerView={1}
                        breakpoints={{
                            768: {
                                slidesPerView: 3,
                            },
                        }}
                        direction="horizontal"
                        onTouchStart={() => setIsSwiping(true)}
                        onTouchEnd={() => setIsSwiping(false)}
                        className={mc("min-w-full h-[calc(75%-8*var(--spacing))] shadow-sm shadow-[rgba(0,0,0,0.5)]", { 'cursor-grabbing': isSwiping, 'cursor-grab': !isSwiping })}
                        loop={true}
                    >
                        {SERVICE_ITEMS.map(({ name, icon, items, imageParams, caption }) =>
                            <SwiperSlide key={`service_item_${name}`}
                                className="w-full aspect-square overflow-hidden"
                                onMouseOver={() => (setCurrentService(caption), setCurrentServiceIcon(icon))}
                                onMouseOut={() => (setCurrentService(''), setCurrentServiceIcon(null))}
                            >
                                <ServiceCard name={name} items={items} icon={icon} imageParams={imageParams}/>
                            </SwiperSlide>
                        )}
                    </Swiper>
                    <div className="absolute top-0 -right-8 md:-right-16 w-8 md:w-16 h-full border-b-2 border-brand bg-lighter -mr-[2px] box-content"></div>
                </div>
            </div>

            <div className="relative h-[40px] md:h-[60px] px-4 md:px-8">
                <div className="absolute top-0 -left-4 md:-left-8 w-8 md:w-16 h-full border-t-2 border-r-2 border-brand bg-lighter box-content"></div>
                <div className="border-t-2  border-brand w-full h-full grid grid-cols-3 grid-rows-1 bg-lighter">
                    <div className="border-r-2">
                        <button onClick={() => swiper ? swiper.slidePrev() : void 0}
                            className="group w-full h-full flex justify-center items-center col-span-2 gap-2 cursor-pointer hover:bg-brand-light transition-colors"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} size="sm" className="text-brand" />
                            <div className="relative overflow-hidden font-bold uppercase text-xs/[1rem] md:text-md/[1.5rem] whitespace-break-spaces">
                                <span className="block group-hover:-translate-y-full transition-all ease-in-out duration-250">
                                    Prev
                                </span>
                                <span className="absolute top-0 left-0 translate-y-full group-hover:translate-none transition-all ease-in-out duration-250">
                                    Prev
                                </span>
                            </div>
                        </button>
                    </div>
                    <div className=""></div>
                    <div className="border-l-2">
                        <button onClick={() => swiper ? swiper.slideNext() : void 0}
                            className="group w-full h-full flex justify-center items-center col-span-2 gap-2 cursor-pointer hover:bg-brand-light transition-colors">
                            <div className="relative overflow-hidden font-bold uppercase text-xs/[1rem] md:text-md/[1.5rem] whitespace-break-spaces">
                                <span className="block group-hover:-translate-y-full transition-all ease-in-out duration-250">
                                    Next
                                </span>
                                <span className="absolute top-0 left-0 translate-y-full group-hover:translate-none transition-all ease-in-out duration-250">
                                    Next
                                </span>
                            </div>
                            <FontAwesomeIcon icon={faChevronRight} size="sm" className="text-brand" />
                        </button>
                    </div>
                </div>
                <div className="absolute top-0 -right-4 md:-right-8 w-8 md:w-16 h-full border-t-2 border-l-2 border-brand bg-lighter box-content"></div>
            </div>

            <div className="relative h-[80px] md:h-[200px] px-4 md:px-8 bg-linear-to-b from-lighter to-transparent">
                <div className="absolute top-0 -left-4 md:-left-8 w-8 md:w-16 h-full border-y-2 border-brand"></div>
                <div className="border-t-2 border-x-2 border-brand w-full h-full grid grid-cols-1 md:grid-cols-3 grid-rows-1">
                    <div className="hidden md:block border-r-2 border-b-2"></div>
                    <div className="hidden md:block border-b-2"></div>
                    <div className="hidden md:block border-l-2 border-b-2"></div>
                    <div className="md:hidden border-b-2"></div>
                </div>
                <div className="absolute top-0 -right-4 md:-right-8 w-8 md:w-16 h-full border-y-2 border-brand"></div>
            </div>

        </div>
    </section>
}
